import React, { useState, useEffect } from "react";

import { useNavigate, Link } from "react-router-dom";
import { Colors } from "../utils/utils.colors";
import { ContactBlock } from "./subcomponents/Contactblock";
import { Logo } from "./subcomponents/Logo";
import { MainNavigation } from "./subcomponents/Navigation";
import { TopNavigation } from "./subcomponents/TopNavigationButton";
import { NavItem } from "./subcomponents/NavItem";
import { APPCONTACTS } from "../utils/utils.constants";
import { routes } from "../utils/utils.routes";
import { ButtonComponent } from "./subcomponents/ButtonComponent";
import { useAuth } from "../contexts/AuthContext";
import { Mail, Search, Phone, ChevronRight, MapPin } from "lucide-react";

const MarkerIcon = MapPin as any;
const EnvelopeIcon = Mail as any;
const PhoneIcon = Phone as any;
const SearchIcon = Search as any;
const ArrowForward = ChevronRight as any;

export const Header: React.FC = () => {

  const [isScrolled, setIsScrolled] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  useAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".mega-menu-wrapper")) setMenuOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("click", handleClick);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchText.trim()) {
      navigate(`${routes.SEARCH}?q=${encodeURIComponent(searchText.trim())}`);
      setSearchText("");
    }
  };

  const MegaLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
    <Link to={to} onClick={closeMenu}>{children}</Link>
  );

  const MegaExternal: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={closeMenu}>{children}</a>
  );

  return (
    <header
      className="site-header mb-0"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        backgroundColor: "white",
        paddingBottom: "2px",
        transition: "box-shadow 0.3s ease-in-out",
        boxShadow: isScrolled ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
      }}
    >
      <div className="top-bar w-100" style={{ backgroundColor: Colors.primaryColor, color: "white", padding: "8px 20px" }}>
        <div className="top-bar-inner" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "14px", flexWrap: "wrap", gap: 10 }}>
          <TopNavigation />
          <div className="top-right-actions" style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <NavItem href={routes.FAQ} label="FAQ" color={Colors.whiteColor} />
          </div>
        </div>
      </div>

      <div className="middle-bar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="col-lg-5">
          <Logo />
        </div>
        <div className="col-lg-7 d-lg-none">
          <MainNavigation />
        </div>
        <div className="contact-info-blocks col-lg-7" style={{ display: "flex", gap: "40px", padding: "10px 20px", justifyContent: "flex-end", alignItems: "flex-end" }}>
          <ContactBlock icon={<MarkerIcon color={Colors.redColor} size={13} />} label="Addresse" value={APPCONTACTS.address} />
          <ContactBlock icon={<EnvelopeIcon color={Colors.redColor} size={13} />} label="Email" value={APPCONTACTS.email[0]} />
          <ContactBlock icon={<PhoneIcon color={Colors.redColor} size={13} />} label="Téléphone" value={APPCONTACTS.phone[0]} />
        </div>
      </div>

      <div className="main-nav-container" style={{ display: "flex", alignItems: "stretch", marginBottom: "8px", justifyContent: "space-between", padding: "0 10px" }}>
        <div className="mega-menu-wrapper desktop-only-block">
          <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <div style={{ backgroundColor: Colors.darkColor, color: "white", padding: "15px 30px", display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: "10px" }}>Plus d'infos</span>
              <span>
                <ArrowForward style={{ marginLeft: "10px" }} size={13} />
              </span>
            </div>
          </button>
          {menuOpen && (
          <div className="mega-menu-overlay" style={{ display: 'block' }}>
            <div className="mega-menu-content">
              <div className="mega-menu-columns">
                <div className="mega-menu-column">
                  <h3>Formation</h3>
                  <ul>
                    <li><MegaLink to={routes.FACULTIES}>Toutes les facultés</MegaLink></li>
                    <li><MegaLink to={routes.DOMAINES}>Domaines & filières</MegaLink></li>
                    <li><MegaLink to={routes.ADMISSION}>Admission</MegaLink></li>
                    <li><MegaLink to={routes.GUIDE_INSCRIPTION}>Guide d'inscription</MegaLink></li>
                    <li><MegaLink to={routes.FRAIS}>Frais académiques</MegaLink></li>
                    <li><MegaLink to={routes.SPECIAL_PROGRAMS.replace(/\/:slug$/, '')}>Programmes spéciaux</MegaLink></li>
                  </ul>
                </div>
                <div className="mega-menu-column">
                  <h3>Vie étudiante</h3>
                  <ul>
                    <li><MegaLink to={routes.EVENTS}>Événements</MegaLink></li>
                    <li><MegaLink to={routes.SCHEDULES}>Horaires & examens</MegaLink></li>
                    <li><MegaLink to={routes.TENDERS}>Appels d'offres</MegaLink></li>
                    <li><MegaLink to={routes.ANCIENS}>Anciens (Alumni)</MegaLink></li>
                  </ul>
                </div>
                <div className="mega-menu-column">
                  <h3>Publications</h3>
                  <ul>
                    <li><MegaLink to={routes.ARTICLES}>Articles</MegaLink></li>
                    <li><MegaLink to={`${routes.ARTICLES}/activite`}>Activités étudiantes</MegaLink></li>
                    <li><MegaLink to={routes.PARTNERS}>Nos partenaires</MegaLink></li>
                    <li><MegaLink to={routes.FAQ}>Foire aux questions</MegaLink></li>
                  </ul>
                </div>
                <div className="mega-menu-column">
                  <h3>Centres & Recherche</h3>
                  <ul>
                    <li><MegaLink to={routes.CENTRES}>Centres de recherche</MegaLink></li>
                    <li><MegaLink to={routes.BIBLIO}>Bibliothèque</MegaLink></li>
                    <li><MegaLink to={routes.RESSOURCES}>Ressources</MegaLink></li>
                  </ul>
                </div>
                <div className="mega-menu-column">
                  <h3>Écoles</h3>
                  <ul>
                    <li><MegaLink to={routes.KAUTA}>École Primaire Kauta</MegaLink></li>
                    <li><MegaLink to={routes.METANOIA}>École Secondaire Metanoia</MegaLink></li>
                    <li><MegaLink to={routes.AUMONERIE}>Aumônerie universitaire</MegaLink></li>
                  </ul>
                </div>
                <div className="mega-menu-column">
                  <h3>ULPGL</h3>
                  <ul>
                    <li><MegaLink to={routes.ABOUT}>À propos</MegaLink></li>
                    <li><MegaLink to={routes.CONTACTS}>Contact</MegaLink></li>
                    <li><MegaLink to={routes.SEARCH}>Recherche</MegaLink></li>
                    <li><MegaExternal href={routes.COMPLAINTS}>Plaintes</MegaExternal></li>
                    <li><MegaExternal href={routes.TFE}>TFE / SGT</MegaExternal></li>
                    <li><MegaLink to={routes.LOGIN}>Admin</MegaLink></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        <div className="main-navigation desktop-only-block">
          <MainNavigation />
        </div>
        <form onSubmit={submitSearch} className="search-area desktop-only-block" style={{ display: "flex", alignItems: "stretch", border: "1px solid #ddd", overflow: "hidden", height: "54px" }}>
          <input
            data-testid="header-search-input"
            type="text"
            placeholder="Recherche..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ border: "none", lineHeight: "normal", background: "transparent", height: "100%", flex: 1, outline: "none", color: Colors.darkColor, padding: "0 14px" }}
          />
          <ButtonComponent label="" onClick={submitSearch as any} icon={<SearchIcon color={Colors.whiteColor} />} />
        </form>
      </div>
    </header>
  );
};
